import { Box, Flex, ScrollArea, UnstyledButton } from "@mantine/core";
import type { User_role } from "@prisma/client";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { type ReactNode, type FC, useEffect, useState } from "react";
import { CopyrightMemo } from "src/component/element";
import { NotificationBadgeCount } from "src/component/element/Notification/NotificationBadgeCount";
import { SIDEBAR_PATH } from "src/const";
import {
  ADMIN_AUTHORITY_PATH,
  COACH_AUTHORITY_PATH,
  FREE_USER_SIDEBAR_PATH,
} from "src/const/paths";
import { useRecordUserEventMutation } from "src/service/UserEventsApi/UserEventsApi";
import { useGetUserSettingsQuery } from "src/service/UsersApi";

import styles from "./Sidebar.module.scss";

export const Sidebar: FC = () => {
  const { data: user } = useGetUserSettingsQuery();
  const [navItemProps, serNavItemsProps] = useState<
    {
      label: string;
      href: string;
      icon: ReactNode;
    }[]
  >([]);

  // 権限でメニューの内容を変更
  useEffect(() => {
    if (user) {
      const role = user.role;
      const isAdmin =
        role === "ADMIN" ||
        (user.isUpgradedRole &&
          user.userRoles.some(({ role }) => role === "ADMIN"));
      const logoutItem = SIDEBAR_PATH.filter((item) => {
        return item.label === "ログアウト";
      })[0];
      const OmitLogoutItems = SIDEBAR_PATH.filter((item) => {
        return item.label !== "ログアウト";
      });

      if (isAdmin) {
        return serNavItemsProps([
          ...OmitLogoutItems,
          COACH_AUTHORITY_PATH,
          ADMIN_AUTHORITY_PATH,
          logoutItem,
        ]);
      } else if (role === "COACH") {
        return serNavItemsProps([
          ...OmitLogoutItems,
          COACH_AUTHORITY_PATH,
          logoutItem,
        ]);
      } else if (role === "STUDENT") {
        return serNavItemsProps([...SIDEBAR_PATH]);
      } else {
        return serNavItemsProps([...FREE_USER_SIDEBAR_PATH]);
      }
    } else {
      serNavItemsProps([...FREE_USER_SIDEBAR_PATH]);
    }
  }, [user]);

  const navItems = navItemProps.map((navItem, index) => {
    return <SidebarNav key={index} {...navItem} />;
  });
  return (
    <Box component="aside" className={styles.sidebar}>
      <ScrollArea style={{ height: "100vh" }} type="never">
        <div className={styles.sidebarInner}>
          <div className={styles.logoWrap}>
            <Image
              src="/images/logo.svg"
              alt="TANQ BASEのロゴ"
              width={138}
              height={46}
              className={styles.logo}
              priority
            />
          </div>
          {navItems}
          <div className={styles.copyrightWrap}>
            <CopyrightMemo />
          </div>
          <div className={styles.notificationWrapper}>
            <NotificationBadgeCount />
          </div>
        </div>
      </ScrollArea>
    </Box>
  );
};

const SidebarNav = ({
  href,
  icon,
  label,
  roles,
  openNewTab,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  openNewTab?: boolean;
  roles?: User_role[];
}) => {
  const { data: user } = useGetUserSettingsQuery();
  const [record] = useRecordUserEventMutation();
  if (roles?.length && user && !roles.includes(user?.role)) {
    return null;
  }

  return (
    <>
      {label === "ログアウト" ? (
        <UnstyledButton
          className={styles.link}
          onClick={async () => {
            await record({
              action: "CLICK",
              eventType: "LOGOUT",
              category: "ユーザーとの対話",
              label: "ログアウトボタン",
              value: "",
            });
            await signOut();
            localStorage.clear();
          }}
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </UnstyledButton>
      ) : label === "ガイドライン" || label === "部室" ? (
        <Link
          href={href}
          className={styles.link}
          target="_blank"
          passHref
          rel="noopener noreferrer"
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </Link>
      ) : (
        <Link
          href={href}
          className={styles.link}
          target={openNewTab ? "_blank" : undefined}
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </Link>
      )}
    </>
  );
};
